import { worldRoot } from './worldRoot'
/**
 * PlayCanvas station lifecycle system.
 *
 * StationLifecycle — pure TS state machine (no PC dependency). Tracks which
 * runs are spawned/lingering and fires onSpawn/onDespawn callbacks.
 *
 * StationSystem — wraps StationLifecycle with actual PlayCanvas entity
 * creation/destruction. Production state is injected by HqProjectionSystem;
 * the default self-subscription remains for isolated tests and reuse.
 */
import * as pc from 'playcanvas'
import { useArenaStore } from '../../store'
import type {
  Interaction,
  NormalizedRun,
  ProjectedAgent,
  TranscriptEvent,
} from '../../api/types'
import { StationLayout } from './layout'
import {
  FLOOR_SLOT_COUNT,
  floorSlotPosition,
  floorSlotRotation,
  floorSlotZoneId,
} from './floorPrograms'
import type { CharacterHandle } from '../characters'
import type { VoxelAvatarState } from '../avatarState'
import { computeAvatarState } from '../avatarState'
import { hqZoneRoot, spawnDeskClutter } from './dressing'
import { bindRunsToAgents, placeableRosterAgents } from './agentRoster'
import {
  createSharedMonitorDisplay,
  type SharedMonitorDisplay,
} from './monitorDisplay'
import {
  bindStationVisualMaterials,
  applyStationSurfaceStatus,
  createStationVisualMaterials,
  spawnStationVisual,
  STATION_STATUS_COLORS,
  type StationVisual,
  type StationVisualMaterials,
} from './stationVisual'
import {
  spawnLightweightStationFigure,
} from './lightweightFigure'

function getCharacterAvatarState(
  run: NormalizedRun,
  interactions: Interaction[],
  transcripts: TranscriptEvent[],
): VoxelAvatarState {
  // cancelled is not handled by computeAvatarState — treat it as slumped.
  if (run.status === 'cancelled') return 'slumped'
  return computeAvatarState(run, interactions, transcripts)
}

export const DESPAWN_DELAY_MS = 8_000

// Status → [r, g, b] in 0-1 range
export const STATUS_COLORS = STATION_STATUS_COLORS

/** Updates pad diffuse, screen emissive, and lamp emissive/diffuse to reflect the current run status. */
export function applyStatusColors(
  padMat: pc.StandardMaterial,
  screenMat: pc.StandardMaterial,
  lampMat: pc.StandardMaterial,
  status: string,
): void {
  applyStationSurfaceStatus(padMat, screenMat, lampMat, status)
}

// ── Pure lifecycle state machine ──────────────────────────────────────────

export class StationLifecycle {
  private seenRunning = new Set<string>()
  private despawnTimers = new Map<string, ReturnType<typeof setTimeout>>()
  private activeIds = new Set<string>()

  constructor(
    private readonly onSpawn: (runId: string) => void,
    private readonly onDespawn: (runId: string) => void,
  ) {}

  update(runs: NormalizedRun[]): void {
    const runIds = new Set(runs.map((r) => r.id))

    for (const run of runs) {
      // A stale run's agent is presumed fallen: no live desk. If it had one
      // (went stale mid-session), remove it quietly — no death animation.
      if (run.stale && run.status === 'running') {
        if (this.activeIds.has(run.id)) {
          const timer = this.despawnTimers.get(run.id)
          if (timer !== undefined) {
            clearTimeout(timer)
            this.despawnTimers.delete(run.id)
          }
          this.activeIds.delete(run.id)
          this.seenRunning.delete(run.id)
          this.onDespawn(run.id)
        }
        continue
      }
      if (run.status === 'running') {
        this.seenRunning.add(run.id)
        // Cancel any pending despawn timer
        const timer = this.despawnTimers.get(run.id)
        if (timer !== undefined) {
          clearTimeout(timer)
          this.despawnTimers.delete(run.id)
        }
        if (!this.activeIds.has(run.id)) {
          this.activeIds.add(run.id)
          this.onSpawn(run.id)
        }
        continue
      }

      // Terminal run that was previously seen as running → linger then despawn
      if (
        (run.status === 'completed' || run.status === 'failed' || run.status === 'cancelled') &&
        this.seenRunning.has(run.id) &&
        this.activeIds.has(run.id) &&
        !this.despawnTimers.has(run.id)
      ) {
        this.despawnTimers.set(
          run.id,
          setTimeout(() => {
            this.despawnTimers.delete(run.id)
            this.activeIds.delete(run.id)
            this.seenRunning.delete(run.id)
            this.onDespawn(run.id)
          }, DESPAWN_DELAY_MS),
        )
      }
    }

    // Identify active stations (no pending timer) whose runs have disappeared —
    // must be collected BEFORE timer pruning so the check is accurate.
    const immediatelyGone: string[] = []
    for (const runId of [...this.activeIds]) {
      if (!runIds.has(runId) && !this.despawnTimers.has(runId)) {
        immediatelyGone.push(runId)
      }
    }

    // Cancel timers for lingering stations that disappeared; remove from active
    // and fire onDespawn so the entity is destroyed (not left orphaned).
    for (const [runId, timer] of [...this.despawnTimers.entries()]) {
      if (!runIds.has(runId)) {
        clearTimeout(timer)
        this.despawnTimers.delete(runId)
        this.activeIds.delete(runId)
        this.onDespawn(runId)
      }
    }

    // Evict seenRunning for runs that are no longer returned by the API.
    for (const runId of [...this.seenRunning]) {
      if (!runIds.has(runId)) this.seenRunning.delete(runId)
    }

    // Despawn stations that were running (no linger timer) but disappeared.
    for (const runId of immediatelyGone) {
      this.activeIds.delete(runId)
      this.seenRunning.delete(runId)
      this.onDespawn(runId)
    }
  }

  getActiveIds(): string[] {
    return [...this.activeIds]
  }

  destroy(): void {
    for (const timer of this.despawnTimers.values()) clearTimeout(timer)
    this.despawnTimers.clear()
  }
}

/** Stable empty identities — `?? []` would mint a new array every call. */
const EMPTY_INTERACTIONS: Interaction[] = []
const EMPTY_TRANSCRIPTS: TranscriptEvent[] = []

// ── PlayCanvas station system ─────────────────────────────────────────────

interface StatusMaterials {
  padMat: pc.StandardMaterial
  screenMat: pc.StandardMaterial
  lampMat: pc.StandardMaterial
  monitorMat: pc.StandardMaterial
  visual: StationVisualMaterials
}

interface StationEntry {
  entity: pc.Entity
  visual: StationVisual
  clutterSpawned: boolean
}

export class StationSystem {
  private app!: pc.Application
  private lifecycle!: StationLifecycle
  // Multi-floor slot set: production agents occupy the programmed L1/L2 pods
  // instead of piling onto reception at L0.
  private layout = new StationLayout(FLOOR_SLOT_COUNT, floorSlotPosition, floorSlotRotation)
  private entries = new Map<string, StationEntry>()
  private characterHandles = new Map<string, CharacterHandle>()
  private positions = new Map<string, [number, number, number]>()
  private currentRuns: NormalizedRun[] = []
  private unsubscribe?: () => void
  // Shared material pool — one set per status, created lazily on first use.
  private sharedMats = new Map<string, StatusMaterials>()
  private monitorDisplay?: SharedMonitorDisplay
  private ownsMonitorDisplay = false
  private drivesMonitorDisplay = true
  private stationBatchGroupId?: number
  private onAppUpdate = (dt: number): void => this.frame(dt)

  /** Returns (or lazily creates) shared materials for a given status. */
  private getOrCreateMats(status: string): StatusMaterials {
    if (this.sharedMats.has(status)) return this.sharedMats.get(status)!
    const visual = createStationVisualMaterials(
      status,
      (material) => this.monitorDisplay?.attach(material),
    )
    const mats: StatusMaterials = {
      padMat: visual.pad,
      screenMat: visual.divider,
      lampMat: visual.lamp,
      monitorMat: visual.monitor,
      visual,
    }
    this.sharedMats.set(status, mats)
    return mats
  }

  init(
    app: pc.Application,
    options: {
      externalProjection?: boolean
      monitorDisplay?: SharedMonitorDisplay
      driveMonitorDisplay?: boolean
    } = {},
  ): void {
    this.app = app
    this.monitorDisplay = options.monitorDisplay ?? createSharedMonitorDisplay(app)
    this.ownsMonitorDisplay = options.monitorDisplay === undefined
    this.drivesMonitorDisplay = options.driveMonitorDisplay ?? true
    this.stationBatchGroupId = app.batcher
      ?.addGroup('live-run-stations', true, 128)
      .id

    this.lifecycle = new StationLifecycle(
      (runId) => this.spawnEntity(runId),
      (runId) => this.despawnEntity(runId),
    )

    if (!options.externalProjection) this.unsubscribe = useArenaStore.subscribe((state, prev) => {
      // Only a change in the RUN SET can move desks. Transcript events arrive
      // per streamed token; running the full lifecycle + layout pass on each
      // one is pure waste (and re-writes character transforms).
      const structural = state.runs !== prev.runs || state.agents !== prev.agents
      const animOnly =
        state.interactionsByRun !== prev.interactionsByRun ||
        state.transcriptsByRun !== prev.transcriptsByRun
      if (structural) {
        this.handleRunsUpdate(
          bindRunsToAgents(placeableRosterAgents(state.agents), state.runs).unboundRuns,
          state.interactionsByRun,
          state.transcriptsByRun,
        )
      } else if (animOnly) {
        this.applyAnimStates(state.runs, state.interactionsByRun, state.transcriptsByRun)
      }
    })

    if (!options.externalProjection) {
      const init = useArenaStore.getState()
      this.projectRemoteState(
        init.runs,
        init.agents,
        init.interactionsByRun,
        init.transcriptsByRun,
      )
    }

    // One application-level update drives the one shared dynamic texture.
    // This remains optional so a partially mocked Application is still valid.
    const evts = app as unknown as { on?: (ev: string, fn: (dt: number) => void) => void }
    if (this.drivesMonitorDisplay) evts.on?.('update', this.onAppUpdate)
  }

  /** P7 seam: one coordinator owns remote-state subscriptions in production. */
  projectRemoteState(
    runs: NormalizedRun[],
    agents: ProjectedAgent[],
    interactionsByRun: Record<string, Interaction[]>,
    transcriptsByRun: Record<string, TranscriptEvent[]>,
  ): void {
    this.handleRunsUpdate(
      bindRunsToAgents(placeableRosterAgents(agents), runs).unboundRuns,
      interactionsByRun,
      transcriptsByRun,
    )
  }

  /** Cheap token/interaction-only update; does not rerun layout or lifecycle. */
  projectAgentActivity(
    interactionsByRun: Record<string, Interaction[]>,
    transcriptsByRun: Record<string, TranscriptEvent[]>,
  ): void {
    this.applyAnimStates(this.currentRuns, interactionsByRun, transcriptsByRun)
  }

  /** Advance one shared scrolling monitor texture while any desk is running. */
  frame(dt: number): void {
    const runningStationCount = this.currentRuns.reduce(
      (count, run) =>
        count + Number(run.status === 'running' && this.entries.has(run.id)),
      0,
    )
    this.monitorDisplay?.update(dt, runningStationCount)
  }

  getPositions(): Map<string, [number, number, number]> {
    // Return WORLD positions, not the layout-local ones. The desk entities are
    // parented under the interior island root (z ~= -1000), so their local
    // office coordinates are NOT their world coordinates. Labels, stream beams
    // and positional audio all project these through worldToScreen / 3D space,
    // so handing back local coords made the workflow-status overlays render on
    // the district street where the desks used to be. The entity's actual world
    // position already includes the island offset.
    const world = new Map<string, [number, number, number]>()
    for (const [runId, entry] of this.entries) {
      const p = entry.entity.getPosition()
      world.set(runId, [p.x, p.y, p.z])
    }
    return world
  }

  private handleRunsUpdate(
    runs: NormalizedRun[],
    interactionsByRun: Record<string, Interaction[]>,
    transcriptsByRun: Record<string, TranscriptEvent[]>,
  ): void {
    this.currentRuns = runs
    this.lifecycle.update(runs)
    this.syncPositions()

    // Update lamp/screen colors and character anim state for existing stations
    for (const run of runs) {
      const entry = this.entries.get(run.id)
      if (entry) {
        // Swap to shared materials for the current status
        const mats = this.getOrCreateMats(run.status)
        bindStationVisualMaterials(entry.visual, mats.visual)
        if (this.stationBatchGroupId !== undefined) {
          this.app.batcher?.markGroupDirty(this.stationBatchGroupId)
        }
      }
    }
    this.applyAnimStates(runs, interactionsByRun, transcriptsByRun)
  }

  /** Recompute + apply each character's animation state (no layout work). */
  private applyAnimStates(
    runs: NormalizedRun[],
    interactionsByRun: Record<string, Interaction[]>,
    transcriptsByRun: Record<string, TranscriptEvent[]>,
  ): void {
    for (const run of runs) {
      const interactions = interactionsByRun[run.id] ?? EMPTY_INTERACTIONS
      const transcripts = transcriptsByRun[run.id] ?? EMPTY_TRANSCRIPTS
      this.characterHandles.get(run.id)?.setAnimState(
        getCharacterAvatarState(run, interactions, transcripts),
      )
    }
  }

  /** Last placement written per run — suppresses redundant transform writes. */
  private lastPlacementKey = new Map<string, string>()

  private syncPositions(): void {
    const activeIds = [...this.entries.keys()]
    this.positions = this.layout.assign(activeIds)
    for (const [runId, entry] of this.entries) {
      const pos = this.positions.get(runId)
      if (pos) {
        const slot = this.layout.getSlot(runId)
        const rotY = this.layout.getRotation(runId)

        // Live station behavior remains system-owned, but the scene graph is
        // partitioned by the same identity-transform zone roots as static
        // furniture so culling/ownership audits describe one coherent pod.
        if (slot !== undefined) {
          const parent = hqZoneRoot(this.app, floorSlotZoneId(slot))
          if (entry.entity.parent !== parent) parent.addChild(entry.entity)
          const character = this.characterHandles.get(runId)?.entity
          if (character && character.parent !== parent) parent.addChild(character)
        }

        // Deterministic clutter pass — once per station, keyed by slot id.
        if (!entry.clutterSpawned && slot !== undefined) {
          entry.clutterSpawned = true
          spawnDeskClutter(
            this.app,
            entry.entity,
            slot,
            this.stationBatchGroupId,
          )
        }
        entry.entity.setLocalPosition(pos[0], pos[1], pos[2])
        entry.entity.setEulerAngles(0, rotY, 0)

        // The character owns its own transform from here: grounded at the
        // standing work spot, looking around while waiting, walking on completion.
        // Re-applying an UNCHANGED placement rewrites the character's euler
        // and clobbers the waiting look-around sweep for a frame, so only
        // write when the desk actually moved.
        const placementKey = `${pos[0]},${pos[1]},${pos[2]},${rotY}`
        if (this.lastPlacementKey.get(runId) !== placementKey) {
          this.lastPlacementKey.set(runId, placementKey)
          this.characterHandles.get(runId)?.setStation({
            position: [pos[0], pos[1], pos[2]],
            rotY,
          })
        }
      }
    }
  }

  private spawnEntity(runId: string): void {
    const run = this.currentRuns.find((r) => r.id === runId)
    const { padMat, screenMat, lampMat, monitorMat } = this.getOrCreateMats(run?.status ?? 'running')

    const root = new pc.Entity(`station-${runId}`)

    worldRoot(this.app).addChild(root)
    const visual = spawnStationVisual(this.app, root, {
      pad: padMat,
      divider: screenMat,
      lamp: lampMat,
      monitor: monitorMat,
    }, { batchGroupId: this.stationBatchGroupId })

    this.entries.set(runId, {
      entity: root,
      visual,
      clutterSpawned: false,
    })

    const charHandle = spawnLightweightStationFigure(
      this.app,
      this.stationBatchGroupId,
      // Reuse the pod's shared status vocabulary instead of minting a second
      // material dialect for its identity-free workload figure.
      { body: screenMat, head: lampMat },
      runId,
    )
    this.characterHandles.set(runId, charHandle)
  }

  private despawnEntity(runId: string): void {
    const charHandle = this.characterHandles.get(runId)
    if (charHandle) {
      charHandle.destroy()
      this.characterHandles.delete(runId)
    }
    const entry = this.entries.get(runId)
    if (entry) {
      entry.entity.destroy()
      this.entries.delete(runId)
    }
    this.syncPositions()
  }

  destroy(): void {
    const evts = this.app as unknown as { off?: (ev: string, fn: (dt: number) => void) => void } | undefined
    if (this.drivesMonitorDisplay) evts?.off?.('update', this.onAppUpdate)
    this.unsubscribe?.()
    this.lifecycle?.destroy()
    for (const handle of this.characterHandles.values()) handle.destroy()
    this.characterHandles.clear()
    for (const entry of this.entries.values()) entry.entity.destroy()
    this.entries.clear()
    this.sharedMats.clear()
    if (this.stationBatchGroupId !== undefined) {
      this.app.batcher?.removeGroup(this.stationBatchGroupId)
      this.stationBatchGroupId = undefined
    }
    if (this.ownsMonitorDisplay) this.monitorDisplay?.destroy()
    this.monitorDisplay = undefined
  }
}
